import React, { useRef, useState, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { APP_PERMISSIONS } from '../../constants';
import { usePermissions } from '../../hooks/usePermissions';
import {
  announceToScreenReader,
  keyboardNavigation,
  ariaUtils,
} from '../../utils/accessibility';
import { downloadFile, serializeCsv } from '../../utils/fileExport';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { Card } from '../common/Card';
import { PermissionGate } from '../common/PermissionGate';

import { EvaluationLifecycleRoute } from './EvaluationLifecycleRoute';

import type { DashboardMetrics } from '../../hooks/useAnalytics';

interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: string;
  href: string;
  color: 'blue' | 'green' | 'yellow' | 'purple';
  permissions?: string[];
  roles?: string[];
}

// Role-specific quick actions
const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'new-evaluation',
    title: 'Start New Evaluation',
    description: 'Begin a new teacher evaluation process',
    icon: '📝',
    href: '/evaluations/create',
    color: 'blue',
    permissions: [APP_PERMISSIONS.CREATE_EVALUATION],
    roles: [
      'teacher',
      'evaluator',
      'assistant_principal',
      'principal',
      'school_admin',
      'district_admin',
      'super_admin',
    ],
  },
  {
    id: 'view-reports',
    title: 'View Reports',
    description: 'Access performance and analytics reports',
    icon: '📊',
    href: '/reports',
    color: 'green',
    permissions: [APP_PERMISSIONS.VIEW_ANALYTICS],
  },
  {
    id: 'manage-users',
    title: 'Manage Users',
    description: 'Add, edit, or remove user accounts',
    icon: '👥',
    href: '/users',
    color: 'purple',
    permissions: [APP_PERMISSIONS.CREATE_USER, APP_PERMISSIONS.UPDATE_USER],
    roles: ['principal', 'school_admin', 'district_admin', 'super_admin'],
  },
  {
    id: 'district-overview',
    title: 'District Overview',
    description: 'View district-wide analytics and reports',
    icon: '🏢',
    href: '/districts',
    color: 'blue',
    permissions: [APP_PERMISSIONS.READ_DISTRICT],
    roles: ['district_admin', 'super_admin'],
  },
];

// Accessible Quick Actions Component
export const RoleBasedQuickActions: React.FC = () => {
  const { hasPermission, hasRole } = usePermissions();
  const navigate = useNavigate();
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const actionsRef = useRef<HTMLDivElement>(null);

  // Filter actions based on user permissions and roles
  const availableActions = QUICK_ACTIONS.filter(action => {
    // Check permissions
    const allowByPermission = action.permissions
      ? action.permissions.some(permission => hasPermission(permission))
      : true;

    // Check roles
    const hasRequiredRole = action.roles
      ? action.roles.some(role => hasRole(role))
      : true;

    return allowByPermission && hasRequiredRole;
  });

  const handleActionClick = useCallback(
    (action: QuickAction) => {
      announceToScreenReader(`Navigating to ${action.title}`, 'polite');
      navigate(action.href);
    },
    [navigate]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, actionIndex: number) => {
      const actionElements = Array.from(
        actionsRef.current?.querySelectorAll('[data-action-card]') || []
      ) as HTMLElement[];

      keyboardNavigation.handleArrowKeys(
        event.nativeEvent,
        actionElements,
        actionIndex,
        newIndex => {
          setFocusedIndex(newIndex);
        }
      );

      // Handle activation
      keyboardNavigation.handleActivation(event.nativeEvent, () => {
        const action = availableActions[actionIndex];
        if (action) {
          handleActionClick(action);
        }
      });
    },
    [availableActions, handleActionClick]
  );

  if (availableActions.length === 0) {
    return (
      <section
        aria-labelledby="quick-actions-heading"
        className="bg-gray-50 border border-gray-200 rounded-lg p-6"
      >
        <h2
          id="quick-actions-heading"
          className="text-lg font-medium text-gray-900 mb-4"
        >
          Quick Actions
        </h2>
        <p className="text-gray-600">
          No actions are available for your current role and permissions.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="quick-actions-heading">
      <h2
        id="quick-actions-heading"
        className="text-lg font-medium text-gray-900 mb-4"
      >
        Quick Actions
        <span className="sr-only">
          {availableActions.length} action
          {availableActions.length !== 1 ? 's' : ''} available
        </span>
      </h2>
      <div
        ref={actionsRef}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
        role="group"
        aria-label="Available quick actions"
      >
        {availableActions.map((action, index) => (
          <Card
            key={action.id}
            data-action-card
            className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2 ${
              focusedIndex === index ? 'ring-2 ring-blue-500 ring-offset-2' : ''
            }`}
            tabIndex={0}
            role="button"
            aria-label={`${action.title}: ${action.description}`}
            aria-describedby={`action-${action.id}-description`}
            onKeyDown={e => handleKeyDown(e, index)}
            onClick={() => handleActionClick(action)}
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(-1)}
          >
            <div className="flex items-center space-x-3">
              <span
                className="text-2xl"
                role="img"
                aria-label={action.title}
                aria-hidden="false"
              >
                {action.icon}
              </span>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{action.title}</h3>
                <p
                  id={`action-${action.id}-description`}
                  className="text-sm text-gray-600"
                >
                  {action.description}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

// Accessible Teacher Dashboard Component
export const TeacherDashboard: React.FC = () => {
  const headingId = ariaUtils.generateId('teacher-stats');

  // Mock teacher data - would come from API
  const teacherData = {
    evaluationsCompleted: 8,
    evaluationsPending: 2,
    studentsCount: 24,
    averageScore: 3.8,
  };

  const recentActivity = [
    {
      id: '1',
      type: 'evaluation' as const,
      description: 'Evaluation submitted for review',
      timestamp: '2 hours ago',
    },
    {
      id: '2',
      type: 'feedback' as const,
      description: 'New feedback received from principal',
      timestamp: '1 day ago',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Teacher Statistics Section */}
      <section aria-labelledby={headingId}>
        <h2 id={headingId} className="text-lg font-medium text-gray-900 mb-4">
          My Teaching Overview
        </h2>
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
          role="group"
          aria-label="Teaching statistics summary"
        >
          <Card
            className="p-4"
            role="region"
            aria-label="Evaluations completed"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  My Evaluations
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {teacherData.evaluationsCompleted}
                  <span className="sr-only">evaluations completed</span>
                </p>
              </div>
              <div className="text-2xl" aria-hidden="true">
                ✅
              </div>
            </div>
          </Card>

          <Card className="p-4" role="region" aria-label="Students assigned">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">My Students</p>
                <p className="text-2xl font-bold text-gray-900">
                  {teacherData.studentsCount}
                  <span className="sr-only">students in my classes</span>
                </p>
              </div>
              <div className="text-2xl" aria-hidden="true">
                👨‍🎓
              </div>
            </div>
          </Card>

          <Card className="p-4" role="region" aria-label="Pending reviews">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Pending Reviews
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {teacherData.evaluationsPending}
                  <span className="sr-only">evaluations pending review</span>
                </p>
              </div>
              <div className="text-2xl" aria-hidden="true">
                ⏳
              </div>
            </div>
          </Card>

          <Card
            className="p-4"
            role="region"
            aria-label="Average performance score"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avg. Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {teacherData.averageScore}
                  <span className="sr-only">out of 4 points average score</span>
                </p>
              </div>
              <div className="text-2xl" aria-hidden="true">
                ⭐
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Recent Activity Section */}
      <section aria-labelledby="teacher-activity-heading">
        <h2
          id="teacher-activity-heading"
          className="text-lg font-medium text-gray-900 mb-4"
        >
          Recent Activity
          <span className="sr-only">
            {recentActivity.length} recent activities
          </span>
        </h2>
        <Card className="p-6" role="region" aria-label="Recent activities list">
          <div className="space-y-3" role="list">
            {recentActivity.map(activity => (
              <div
                key={activity.id}
                className="flex items-start space-x-3 border-b border-gray-100 pb-3 last:border-b-0"
                role="listitem"
              >
                <Badge
                  variant={activity.type === 'evaluation' ? 'success' : 'info'}
                  aria-label={`Activity type: ${activity.type}`}
                >
                  {activity.type}
                </Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">
                    {activity.description}
                  </p>
                  <p className="text-xs text-gray-500">
                    <time dateTime={activity.timestamp}>
                      {activity.timestamp}
                    </time>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
};

interface AdminDashboardProps {
  data: DashboardMetrics;
}

const formatActivityTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

// Accessible Admin Dashboard Component
export const AdminDashboard: React.FC<AdminDashboardProps> = ({ data }) => {
  const { isSchoolAdmin, isDistrictAdmin } = usePermissions();
  const headingId = ariaUtils.generateId('admin-overview');

  const metrics = {
    totalEvaluations: data.evaluations.total,
    pendingActions: data.evaluations.pending,
    activeUsers: data.users.active,
    totalSchools: data.schools.total,
    totalTeachers: data.teachers.total,
  };

  const recentActivity = data.recent_activity;

  const visibleMetrics = [
    {
      key: 'total_evaluations',
      label: 'Total Evaluations',
      value: metrics.totalEvaluations,
    },
    {
      key: 'pending_actions',
      label: 'Pending Actions',
      value: metrics.pendingActions,
    },
    {
      key: 'active_users',
      label: 'Active Users',
      value: metrics.activeUsers,
    },
    ...(isDistrictAdmin
      ? [
          {
            key: 'total_schools',
            label: 'Total Schools',
            value: metrics.totalSchools,
          },
          {
            key: 'total_teachers',
            label: 'Total Teachers',
            value: metrics.totalTeachers,
          },
        ]
      : []),
  ];

  const handleExportData = (format: 'csv' | 'json') => {
    const scope = data.scope;
    const dateStamp = new Date().toISOString().slice(0, 10);

    announceToScreenReader(
      `Starting ${format.toUpperCase()} data export`,
      'polite'
    );

    if (format === 'csv') {
      const metricRows = visibleMetrics.map(metric => [
        'metric',
        metric.label,
        metric.value,
        '',
        '',
      ]);
      const activityRows = recentActivity.map(activity => [
        'activity',
        activity.type,
        '',
        activity.description,
        activity.timestamp,
      ]);
      const csv = serializeCsv(
        ['Section', 'Name', 'Value', 'Description', 'Timestamp'],
        [...metricRows, ...activityRows]
      );

      downloadFile(
        `${scope}-dashboard-${dateStamp}.csv`,
        csv,
        'text/csv;charset=utf-8'
      );
    } else {
      const payload = {
        scope,
        exported_at: new Date().toISOString(),
        metrics: Object.fromEntries(
          visibleMetrics.map(metric => [metric.key, metric.value])
        ),
        recent_activity: recentActivity,
      };

      downloadFile(
        `${scope}-dashboard-${dateStamp}.json`,
        JSON.stringify(payload, null, 2),
        'application/json'
      );
    }

    announceToScreenReader('Data export completed successfully', 'polite');
  };

  if (!isSchoolAdmin && !isDistrictAdmin) {
    return (
      <div
        className="text-center py-12"
        role="region"
        aria-label="Access restricted message"
      >
        <div className="max-w-md mx-auto">
          <div className="text-6xl mb-4" aria-hidden="true">
            🔒
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Restricted
          </h3>
          <p className="text-gray-600">
            Your account doesn't have the necessary permissions to view this
            dashboard. Please contact your administrator for access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Admin Dashboard Header */}
      <header className="flex justify-between items-center">
        <h2 id={headingId} className="text-xl font-semibold text-gray-900">
          {isDistrictAdmin ? 'District Overview' : 'School Overview'}
        </h2>
        <PermissionGate permission={APP_PERMISSIONS.EXPORT_REPORTS}>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleExportData('csv')}
              aria-describedby="export-help"
            >
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExportData('json')}
              aria-describedby="export-help"
            >
              Export JSON
            </Button>
          </div>
          <span id="export-help" className="sr-only">
            Download visible dashboard metrics and recent activity
          </span>
        </PermissionGate>
      </header>

      <EvaluationLifecycleRoute activeStage="observation" />

      {/* Metrics Grid */}
      <section aria-labelledby={headingId}>
        <div
          className="grid grid-cols-2 gap-3 lg:grid-cols-4"
          role="group"
          aria-label="Administrative metrics summary"
        >
          <Card
            className="p-3"
            role="region"
            aria-label="Active users metric"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--se-ink-muted)]">
              Active users
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--se-ink)]">
              {metrics.activeUsers}
            </p>
          </Card>

          {isDistrictAdmin && (
            <Card className="p-3" role="region" aria-label="Total schools metric">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--se-ink-muted)]">
                Schools
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--se-ink)]">
                {metrics.totalSchools}
              </p>
            </Card>
          )}

          {isDistrictAdmin && (
            <Card
              className="p-3"
              role="region"
              aria-label="Total teachers metric"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--se-ink-muted)]">
                Teachers
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--se-ink)]">
                {metrics.totalTeachers}
              </p>
            </Card>
          )}

          <Card
            className="p-3"
            role="region"
            aria-label="Total evaluations metric"
          >
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--se-ink-muted)]">
              Evaluations
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--se-ink)]">
              {metrics.totalEvaluations}
            </p>
          </Card>

          {isDistrictAdmin && (
            <>
              <Card
                className="hidden p-3 lg:block"
                role="region"
                aria-label="Pending actions metric"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--se-ink-muted)]">
                  Pending actions
                </p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-[var(--se-ink)]">
                  {metrics.pendingActions}
                </p>
              </Card>
            </>
          )}
        </div>
      </section>

      {/* Recent Activity */}
      <section aria-labelledby="admin-activity-heading">
        <Card
          className="p-6"
          role="region"
          aria-label="Administrative activity feed"
        >
          <h3
            id="admin-activity-heading"
            className="text-lg font-medium text-gray-900 mb-4"
          >
            Recent Activity
            <span className="sr-only">
              {recentActivity.length} recent administrative activities
            </span>
          </h3>
          {recentActivity.length === 0 ? (
            <p className="text-sm text-gray-600" role="status">
              No recent activity is available for this scope.
            </p>
          ) : (
            <div className="space-y-3" role="list">
              {recentActivity.map(activity => (
                <div
                  key={activity.id}
                  className="flex items-start space-x-3"
                  role="listitem"
                >
                  <Badge
                    variant={
                      activity.type === 'evaluation' ? 'success' : 'info'
                    }
                    aria-label={`Activity type: ${activity.type}`}
                  >
                    {activity.type}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">
                      {activity.description}
                    </p>
                    <p className="text-xs text-gray-500">
                      <time dateTime={activity.timestamp}>
                        {formatActivityTimestamp(activity.timestamp)}
                      </time>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </section>
    </div>
  );
};
